import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  // MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const BarContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  top: 16,
  right: 16,
  left: 'auto',
  zIndex: 1600,
  padding: theme.spacing(2.25, 2.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[8],
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  maxWidth: 520,
  [theme.breakpoints.down('sm')]: {
    top: 8,
    right: 8,
    left: 8,
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2)
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1.5),
  [theme.breakpoints.down('sm')]: {
    flexGrow: 1
  }
}));

/**
 * Tutorial Bar Component
 * Tiny bar shown for subsequent feature tutorials in the same session
 */
const TutorialBar = ({
  featureName,
  onShowNow,
  onLater,
  onDontShow,
  onClose,
  otherTutorials = []
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const timerRef = useRef(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    if (timeLeft <= 0) {
      clearTimer();
      setTimeLeft(0);
      if (onClose) {
        onClose();
      }
    }
  }, [clearTimer, timeLeft, onClose]);

  const handleShowNow = React.useCallback(() => {
    clearTimer();
    setTimeLeft(0);
    if (onShowNow) {
      onShowNow();
    }
  }, [clearTimer, onShowNow]);

  // const handleMenuOpen = (event) => {
  //   event.stopPropagation();
  //   setMenuAnchor(event.currentTarget);
  // };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDontShow = () => {
    clearTimer();
    if (onDontShow) {
      onDontShow();
    }
    handleMenuClose();
  };

  const handleOtherTutorial = (tutorial) => {
    if (tutorial.onClick) {
      tutorial.onClick();
    }
    handleMenuClose();
  };

  return (
    <BarContainer
      elevation={3}
      role="button"
      tabIndex={0}
      onClick={handleShowNow}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleShowNow();
        }
      }}
      sx={{ cursor: 'pointer' }}
    >
      <Box flex={1} minWidth={0}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          noWrap
          sx={{ lineHeight: 1.4 }}
        >
          New tutorial ready: <Box component="span" sx={{ fontWeight: 700 }}>{featureName}</Box>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, display: 'block', lineHeight: 1.5 }}
        >
          Find all tutorials in <strong>Help → Tutorials</strong>. This reminder will close in{' '}
          <strong>{timeLeft}s</strong>.
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            height: 6,
            backgroundColor: 'rgba(0,0,0,0.08)',
            borderRadius: 999,
            overflow: 'hidden',
            width: '100%'
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${(timeLeft / 30) * 100}%`,
              background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              transition: 'width 1s linear'
            }}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        gap={1.25}
        flexShrink={0}
        sx={{
          alignSelf: { xs: 'stretch', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          width: { xs: '100%', sm: 'auto' }
        }}
      >
        <ActionButton
          size="small"
          variant="outlined"
          startIcon={<ScheduleIcon />}
          onClick={(event) => {
            event.stopPropagation();
            clearTimer();
            if (onLater) {
              onLater();
            }
          }}
        >
          Later
        </ActionButton>
        <Box display="flex" alignItems="center" gap={0.5}>
          {/* <IconButton
            size="small"
            onClick={handleMenuOpen}
            aria-label="More options"
          >
            <MoreVertIcon />
          </IconButton> */}
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              clearTimer();
              if (onClose) {
                onClose();
              }
            }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleDontShow();
          }}
        >
          <BlockIcon fontSize="small" sx={{ mr: 1 }} />
          <ListItemText primary="Don't show for this feature" />
        </MenuItem>
        {otherTutorials.length > 0 && [
          <MenuItem disabled key="other-tutorials-header">
            <ListItemText primary="Other tutorials" secondary="Click to view" />
          </MenuItem>,
          ...otherTutorials.map((tutorial, index) => (
            <MenuItem
              key={tutorial.featureId || index}
              onClick={(event) => {
                event.stopPropagation();
                handleOtherTutorial(tutorial);
              }}
            >
              <ListItemText
                primary={tutorial.title}
                secondary={tutorial.description}
              />
            </MenuItem>
          ))
        ]}
      </Menu>
    </BarContainer>
  );
};

export default TutorialBar;

